import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const ToastContext = createContext(null);

/* Errors stay longer — they usually need reading and acting on. */
const DURATION = { success: 4000, info: 5000, error: 8000 };
const MAX_VISIBLE = 4;
const EXIT_MS = 220;

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

let nextId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const clearTimer = useCallback((id) => {
    const timer = timers.current.get(id);
    if (timer?.handle) clearTimeout(timer.handle);
    timers.current.delete(id);
  }, []);

  const remove = useCallback((id) => {
    clearTimer(id);
    // Mark it leaving so the exit animation can run, then drop it.
    setToasts((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), reducedMotion() ? 0 : EXIT_MS);
  }, [clearTimer]);

  const startTimer = useCallback((id, duration) => {
    if (!duration) return;
    clearTimer(id);
    timers.current.set(id, {
      handle: setTimeout(() => remove(id), duration),
      endsAt: Date.now() + duration,
      duration,
    });
  }, [clearTimer, remove]);

  const push = useCallback((kind, message, options = {}) => {
    const text = typeof message === 'string' ? message.trim() : message;
    if (!text) return null;

    const duration = options.duration ?? DURATION[kind] ?? DURATION.info;
    let id = null;

    setToasts((list) => {
      // The same message firing twice counts up instead of stacking duplicates.
      const twin = list.find((t) => !t.leaving && t.kind === kind && t.message === text);
      if (twin) {
        id = twin.id;
        return list.map((t) => (t.id === twin.id ? { ...t, count: t.count + 1 } : t));
      }
      id = ++nextId;
      const toast = { id, kind, message: text, title: options.title, count: 1, leaving: false };
      const next = [...list, toast];
      return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
    });

    // setToasts runs synchronously here, so `id` is assigned by this point.
    if (id) startTimer(id, duration);
    return id;
  }, [startTimer]);

  /** Holds the countdown while the pointer or focus is on the stack. */
  const pause = useCallback(() => {
    timers.current.forEach((timer, id) => {
      if (!timer.handle) return;
      clearTimeout(timer.handle);
      timers.current.set(id, { ...timer, handle: null, remaining: Math.max(400, timer.endsAt - Date.now()) });
    });
  }, []);

  const resume = useCallback(() => {
    timers.current.forEach((timer, id) => {
      if (timer.handle) return;
      const remaining = timer.remaining ?? timer.duration;
      timers.current.set(id, {
        handle: setTimeout(() => remove(id), remaining),
        endsAt: Date.now() + remaining,
        duration: timer.duration,
      });
    });
  }, [remove]);

  useEffect(() => {
    const map = timers.current;
    return () => map.forEach((t) => t.handle && clearTimeout(t.handle));
  }, []);

  const toast = useMemo(() => ({
    success: (message, options) => push('success', message, options),
    error: (message, options) => push('error', message, options),
    info: (message, options) => push('info', message, options),
    dismiss: remove,
  }), [push, remove]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={remove} onPause={pause} onResume={resume} />
    </ToastContext.Provider>
  );
}

const ICONS = {
  success: <path d="m5 12.5 4.2 4.2L19 6.8" />,
  error: <><path d="M12 7.5v5.8" /><circle cx="12" cy="16.8" r=".9" fill="currentColor" stroke="none" /></>,
  info: <><path d="M12 11v5.5" /><circle cx="12" cy="7.6" r=".9" fill="currentColor" stroke="none" /></>,
};

const LABEL = { success: 'Success', error: 'Error', info: 'Notice' };

function ToastViewport({ toasts, onDismiss, onPause, onResume }) {
  if (!toasts.length) return null;
  return (
    <div
      className="toast-viewport"
      role="region"
      aria-label="Notifications"
      onMouseEnter={onPause}
      onMouseLeave={onResume}
      onFocus={onPause}
      onBlur={onResume}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast toast-${t.kind} ${t.leaving ? 'is-leaving' : ''}`}
          // Errors interrupt; everything else waits for a gap in the reading.
          role={t.kind === 'error' ? 'alert' : 'status'}
          aria-live={t.kind === 'error' ? 'assertive' : 'polite'}
        >
          <span className="toast-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round">
              {ICONS[t.kind]}
            </svg>
          </span>
          <div className="toast-body">
            <strong className="toast-title">{t.title || LABEL[t.kind]}</strong>
            <p>{t.message}</p>
          </div>
          {t.count > 1 && <span className="toast-count" aria-label={`${t.count} times`}>×{t.count}</span>}
          <button type="button" className="toast-close" onClick={() => onDismiss(t.id)} aria-label="Dismiss">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside a ToastProvider.');
  return ctx;
}
